// Sprint 12 follow-up — single-transcript upload UI for the Meetings
// tab. Replaces the empty-state "POST manually via curl" placeholder
// with a proper modal: title, optional date/attendees, format radio,
// and a file picker that falls back to a paste-text textarea. On
// submit, fires the same POST /projects/:id/meetings the curl flow
// used; the parent re-fetches its list on success.
//
// Multi-file batch upload is deferred — see follow-up issue.

import { useState, FormEvent, ChangeEvent } from 'react';
import { apiFetch } from '../../lib/api';

// Backend cap is 5_000_000 chars on transcriptContent (zod). Mirror
// it client-side so we fail fast with a friendlier message instead of
// shipping the whole payload over the wire.
const MAX_TRANSCRIPT_CHARS = 5_000_000;

interface MeetingUploadDialogProps {
  projectId: string;
  onClose:   () => void;
  onSuccess: () => void;
}

function todayYMD(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function MeetingUploadDialog({ projectId, onClose, onSuccess }: MeetingUploadDialogProps) {
  const [title,        setTitle]        = useState('');
  const [meetingDate,  setMeetingDate]  = useState(todayYMD());
  const [attendees,    setAttendees]    = useState('');
  const [format,       setFormat]       = useState<'vtt' | 'text'>('text');
  const [pasteText,    setPasteText]    = useState('');
  const [fileName,     setFileName]     = useState<string | null>(null);
  const [fileContent,  setFileContent]  = useState<string | null>(null);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState<string | null>(null);

  const effectiveContent = fileContent ?? pasteText;
  const canSubmit = title.trim().length > 0 && effectiveContent.trim().length > 0 && !submitting;

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    if (!file) {
      setFileName(null);
      setFileContent(null);
      return;
    }
    if (file.size > MAX_TRANSCRIPT_CHARS) {
      setError(`File is ${(file.size / 1_000_000).toFixed(2)} MB — max 5 MB.`);
      setFileName(null);
      setFileContent(null);
      e.target.value = '';
      return;
    }
    const text = await file.text();
    if (text.length > MAX_TRANSCRIPT_CHARS) {
      // Belt-and-braces: a multibyte file could decode larger than its
      // byte size suggested. Re-check on the decoded string.
      setError(`Transcript is ${text.length.toLocaleString()} chars — max ${MAX_TRANSCRIPT_CHARS.toLocaleString()}.`);
      setFileName(null);
      setFileContent(null);
      e.target.value = '';
      return;
    }
    setFileName(file.name);
    setFileContent(text);
    // Auto-detect format from extension; user can still override via radio.
    if (file.name.toLowerCase().endsWith('.vtt')) setFormat('vtt');
    else setFormat('text');
  };

  const clearFile = () => {
    setFileName(null);
    setFileContent(null);
    // Reset the input so re-selecting the same file fires onChange again.
    const input = document.getElementById('meeting-upload-file') as HTMLInputElement | null;
    if (input) input.value = '';
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (effectiveContent.length > MAX_TRANSCRIPT_CHARS) {
      setError(`Transcript is ${effectiveContent.length.toLocaleString()} chars — max ${MAX_TRANSCRIPT_CHARS.toLocaleString()}.`);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const attendeeList = attendees
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const body: Record<string, unknown> = {
        title: title.trim(),
        transcriptFormat: format,
        transcriptContent: effectiveContent,
      };
      if (meetingDate) {
        // <input type="date"> gives us YYYY-MM-DD. Construct a Date
        // from local midnight (no trailing Z) so the resulting ISO
        // timestamp, when round-tripped through new Date(...).
        // toLocaleDateString() in the same timezone, renders as the
        // user-selected calendar day. Forcing UTC midnight (`T00:00Z`)
        // shifts the displayed date back one day for users west of
        // UTC — Codex P1 on PR #750.
        body.meetingDate = new Date(`${meetingDate}T00:00:00`).toISOString();
      }
      if (attendeeList.length > 0) body.attendees = attendeeList;

      await apiFetch(`/projects/${projectId}/meetings`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    height: 40,
    background: 'var(--surface-alt)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    padding: '0 12px',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    fontWeight: 500,
    color: 'var(--text)',
    marginBottom: 6,
  };

  const optionalHint: React.CSSProperties = {
    color: 'var(--text-secondary)',
    fontWeight: 400,
  };

  return (
    <div
      data-testid="meeting-upload-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
    >
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />
      <div
        className="relative w-full max-w-[560px] max-h-[90vh] flex flex-col"
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <div
          className="flex-shrink-0 flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid var(--border)' }}
        >
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>
            Upload Meeting Transcript
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ color: 'var(--text-secondary)', fontSize: 18, lineHeight: 1, cursor: 'pointer', background: 'none', border: 'none' }}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
          <div className="flex-1 overflow-y-auto min-h-0 px-6 py-5">
            {error && (
              <div
                data-testid="meeting-upload-error"
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 13,
                  color: 'var(--error)',
                  background: 'rgba(239,68,68,0.06)',
                  border: '1px solid rgba(239,68,68,0.2)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '8px 12px',
                  marginBottom: 16,
                }}
              >
                {error}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label htmlFor="meeting-upload-title" style={labelStyle}>
                Title <span style={{ color: 'var(--error)' }}>*</span>
              </label>
              <input
                id="meeting-upload-title"
                data-testid="meeting-upload-title"
                type="text"
                required
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. Sprint 12 retro"
                maxLength={200}
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="meeting-upload-date" style={labelStyle}>
                  Meeting date <span style={optionalHint}>(optional)</span>
                </label>
                <input
                  id="meeting-upload-date"
                  data-testid="meeting-upload-date"
                  type="date"
                  value={meetingDate}
                  onChange={e => setMeetingDate(e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 2 }}>
                <label htmlFor="meeting-upload-attendees" style={labelStyle}>
                  Attendees <span style={optionalHint}>(comma-separated)</span>
                </label>
                <input
                  id="meeting-upload-attendees"
                  data-testid="meeting-upload-attendees"
                  type="text"
                  value={attendees}
                  onChange={e => setAttendees(e.target.value)}
                  placeholder="alice@acme.com, bob@acme.com"
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Format</label>
              <div style={{ display: 'flex', gap: 16, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="meeting-upload-format"
                    data-testid="meeting-upload-format-text"
                    value="text"
                    checked={format === 'text'}
                    onChange={() => setFormat('text')}
                  />
                  Plain text
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="meeting-upload-format"
                    data-testid="meeting-upload-format-vtt"
                    value="vtt"
                    checked={format === 'vtt'}
                    onChange={() => setFormat('vtt')}
                  />
                  Teams VTT
                </label>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label htmlFor="meeting-upload-file" style={labelStyle}>
                Transcript file <span style={optionalHint}>(.vtt or .txt, max 5 MB)</span>
              </label>
              <input
                id="meeting-upload-file"
                data-testid="meeting-upload-file"
                type="file"
                accept=".vtt,.txt,.md,text/plain,text/vtt"
                onChange={handleFile}
                style={{ ...inputStyle, padding: '8px 12px', height: 'auto' }}
              />
              {fileName && (
                <div
                  data-testid="meeting-upload-file-status"
                  style={{
                    marginTop: 6,
                    fontFamily: 'var(--font-body)',
                    fontSize: 12,
                    color: 'var(--text-secondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <span>Loaded <strong style={{ color: 'var(--text)' }}>{fileName}</strong> ({(fileContent ?? '').length.toLocaleString()} chars)</span>
                  <button
                    type="button"
                    data-testid="meeting-upload-file-clear"
                    onClick={clearFile}
                    style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12, padding: 0 }}
                  >
                    clear
                  </button>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="meeting-upload-paste" style={labelStyle}>
                …or paste transcript text {fileName && <span style={optionalHint}>(ignored — using file)</span>}
              </label>
              <textarea
                id="meeting-upload-paste"
                data-testid="meeting-upload-paste"
                rows={6}
                value={pasteText}
                onChange={e => setPasteText(e.target.value)}
                disabled={fileName !== null}
                placeholder="Paste the meeting transcript here…"
                style={{
                  ...inputStyle,
                  height: 'auto',
                  padding: '10px 12px',
                  fontFamily: 'var(--font-mono, monospace)',
                  fontSize: 12,
                  lineHeight: 1.5,
                  resize: 'vertical',
                  opacity: fileName ? 0.5 : 1,
                }}
              />
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                Extraction runs in the background after upload — decisions, action items, and risks will appear here when ready.
              </p>
            </div>
          </div>

          <div
            className="flex-shrink-0 flex justify-end gap-3 px-6 py-4"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <button
              type="button"
              onClick={onClose}
              data-testid="meeting-upload-cancel"
              style={{
                padding: '8px 16px',
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--text-secondary)',
                background: 'none',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="meeting-upload-submit"
              disabled={!canSubmit}
              style={{
                padding: '8px 16px',
                fontFamily: 'var(--font-display)',
                fontSize: 13,
                fontWeight: 600,
                color: 'white',
                background: 'var(--primary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                opacity: canSubmit ? 1 : 0.5,
              }}
            >
              {submitting ? 'Uploading…' : 'Upload Transcript'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
